import React, { useState } from "react";
import { X, Lock, Mail, User } from "lucide-react";
import axios from "axios";

export default function AuthModal({ isOpen, onClose, onAuthSuccess, initialTab = "login" }) {
  const [activeTab, setActiveTab] = useState(initialTab); // 'login' or 'register'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const endpoint = activeTab === "login" ? "/api/auth/login" : "/api/auth/register";
    const payload = activeTab === "login" 
      ? { email, password } 
      : { email, password, name };

    try {
      const { data } = await axios.post(
        `${API_URL}${endpoint}`,
        payload,
        { withCredentials: true }
      );
      
      onAuthSuccess(data);
      onClose();
      // Clear forms
      setEmail("");
      setPassword("");
      setName("");
    } catch (err) {
      console.error(err);
      const errDetail = err.response?.data?.detail;
      setError(formatApiErrorDetail(errDetail) || err.message || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4">
      <div 
        className="w-full max-w-md bg-neutral-950 border border-neutral-800 p-8 shadow-2xl relative"
        data-testid="auth-modal-container"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-neutral-400 hover:text-white transition-colors focus:outline-none"
          data-testid="auth-modal-close-button"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Branding Header */}
        <div className="text-center mb-8">
          <div className="inline-block border border-white px-3 py-1 font-bebas text-lg tracking-wider mb-2">
            SSA
          </div>
          <h2 className="font-bebas text-3xl tracking-wide uppercase leading-none">
            {activeTab === "login" ? "Enter the Temple" : "Begin Your Training"}
          </h2>
          <p className="text-xs text-neutral-400 font-manrope uppercase tracking-widest mt-1">
            Build Mind • Body • Spirit
          </p>
        </div>

        {/* Tab Toggle */}
        <div className="flex border-b border-neutral-800 mb-6 text-sm uppercase tracking-widest font-semibold">
          <button
            onClick={() => { setActiveTab("login"); setError(""); }}
            className={`flex-1 pb-3 text-center transition-all ${
              activeTab === "login"
                ? "border-b border-white text-white"
                : "text-neutral-500 hover:text-neutral-300"
            }`}
            data-testid="auth-tab-login"
          >
            Sign In
          </button>
          <button
            onClick={() => { setActiveTab("register"); setError(""); }}
            className={`flex-1 pb-3 text-center transition-all ${
              activeTab === "register"
                ? "border-b border-white text-white"
                : "text-neutral-500 hover:text-neutral-300"
            }`}
            data-testid="auth-tab-register"
          >
            Create Account
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div 
            className="mb-4 bg-white text-black px-4 py-3 text-xs uppercase tracking-wider font-semibold"
            data-testid="auth-error-message"
          >
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {activeTab === "register" && (
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">
                Full Name
              </label>
              <div className="relative">
                <User className="w-4 h-4 text-neutral-500 absolute left-0 bottom-2" />
                <input
                  type="text"
                  required
                  placeholder="Elijah Shepherd"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-transparent border-b border-neutral-800 py-2 pl-7 text-sm text-white rounded-none placeholder-neutral-700 focus:border-white focus:outline-none focus:ring-0"
                  data-testid="register-name-input"
                />
              </div>
            </div>
          )}

          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">
              Email Address
            </label>
            <div className="relative">
              <Mail className="w-4 h-4 text-neutral-500 absolute left-0 bottom-2" />
              <input
                type="email"
                required
                placeholder="steward@temple.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border-b border-neutral-800 py-2 pl-7 text-sm text-white rounded-none placeholder-neutral-700 focus:border-white focus:outline-none focus:ring-0"
                data-testid="login-email-input"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">
              Password
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 text-neutral-500 absolute left-0 bottom-2" />
              <input
                type="password"
                required
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent border-b border-neutral-800 py-2 pl-7 text-sm text-white rounded-none placeholder-neutral-700 focus:border-white focus:outline-none focus:ring-0"
                data-testid="login-password-input"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-white text-black py-3 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all duration-300 disabled:opacity-50"
            data-testid={activeTab === "login" ? "login-form-submit-button" : "register-form-submit-button"}
          >
            {loading ? "Processing..." : activeTab === "login" ? "Enter Portal" : "Join the Movement"}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-[11px] text-neutral-500">
            {activeTab === "login" ? (
              <>
                Don't have an account?{" "}
                <button
                  onClick={() => { setActiveTab("register"); setError(""); }}
                  className="text-white underline font-semibold focus:outline-none"
                  data-testid="auth-toggle-register"
                >
                  Register
                </button>
              </>
            ) : (
              <>
                Already registered?{" "}
                <button
                  onClick={() => { setActiveTab("login"); setError(""); }}
                  className="text-white underline font-semibold focus:outline-none"
                  data-testid="auth-toggle-login"
                >
                  Log In
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
